/**
 * SPA mirror of `@flint/domain/compliance/gst-calc/state-codes`.
 *
 * Same reason as `gst-calc-shim.ts`: pulling the domain's helper would
 * pull the whole package's transitive deps into the SPA bundle. The
 * map is small + canonical (GSTN's official state-code list), so a
 * mirror is cheaper than a build-time tree-shake.
 *
 * Consumers: the composer derives the seller's state code from
 * `companies.get` to drive the gst-calc shim's intra/inter mode.
 */

export const STATE_NAME_TO_CODE: Readonly<Record<string, string>> = Object.freeze({
  "Jammu and Kashmir": "01",
  "Himachal Pradesh": "02",
  Punjab: "03",
  Chandigarh: "04",
  Uttarakhand: "05",
  Haryana: "06",
  Delhi: "07",
  Rajasthan: "08",
  "Uttar Pradesh": "09",
  Bihar: "10",
  Sikkim: "11",
  "Arunachal Pradesh": "12",
  Nagaland: "13",
  Manipur: "14",
  Mizoram: "15",
  Tripura: "16",
  Meghalaya: "17",
  Assam: "18",
  "West Bengal": "19",
  Jharkhand: "20",
  Odisha: "21",
  Chhattisgarh: "22",
  "Madhya Pradesh": "23",
  Gujarat: "24",
  "Daman and Diu": "25",
  "Dadra and Nagar Haveli": "26",
  Maharashtra: "27",
  Karnataka: "29",
  Goa: "30",
  Lakshadweep: "31",
  Kerala: "32",
  "Tamil Nadu": "33",
  Puducherry: "34",
  "Andaman and Nicobar Islands": "35",
  Telangana: "36",
  "Andhra Pradesh": "37",
  Ladakh: "38",
  // 96 — exports / supplies to a recipient outside India. The GST portal
  // calls this "Other Country"; we label it "Outside India" for operators.
  "Outside India": "96",
  "Other Territory": "97",
  "Centre Jurisdiction": "99",
});

/** State name → 2-digit GSTIN code, or null when unknown. */
export function stateCodeFromName(name: string | null | undefined): string | null {
  if (!name) return null;
  return STATE_NAME_TO_CODE[name] ?? null;
}

/** First two characters of a GSTIN are the state code. */
export function stateCodeFromGstin(gstin: string | null | undefined): string | null {
  if (!gstin || gstin.length < 2) return null;
  return gstin.slice(0, 2);
}

/** State name from code (reverse lookup). Used by the place-of-supply select. */
export function stateNameFromCode(code: string | null | undefined): string | null {
  if (!code) return null;
  for (const [name, c] of Object.entries(STATE_NAME_TO_CODE)) {
    if (c === code) return name;
  }
  return null;
}

/** All states as `{ code, name }` pairs sorted by name. For pickers. */
export const STATES_FOR_PICKER: ReadonlyArray<{ code: string; name: string }> = Object.freeze(
  Object.entries(STATE_NAME_TO_CODE)
    .map(([name, code]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name)),
);
